import { FC, useCallback } from 'react'
import { useRelationship } from '@wellesley/user'
import { GroupMember, Relationship, User } from '@wellesley/openapi'
import useInfiniteScrollVirtualizer from 'hooks/useInfiniteScrollVirtualizer'
import RowItem, { getRowId, getRowUniqueKey } from './RowItem'
import { ScrolledListProps } from './types'

type VirtualizedScrolledListProps = ScrolledListProps & {
  infiniteScroll: {
    hasNextPage: boolean
    onLoadMore: () => Promise<void>
  }
}

const VirtualizedScrolledList: FC<VirtualizedScrolledListProps> = ({
  list = [],
  className,
  infiniteScroll,
  ...handlers
}) => {
  const { relationship, isLoading: isRelationshipLoading } = useRelationship({
    userId: list?.map(getRowId),
  })

  const prepareUserRowProps = useCallback(
    (item: User | GroupMember) => {
      const id = getRowId(item)

      return {
        item,
        relationship: (relationship as { [key: string]: Relationship })[id],
        isRelationshipLoading,
        className: 'py-2.5',
        ...handlers,
      }
    },
    [relationship, isRelationshipLoading, handlers],
  )

  const virtualizedContainer = useInfiniteScrollVirtualizer({
    type: 'container',
    getItemKey: getRowUniqueKey,
    data: list,
    hasNextPage: infiniteScroll.hasNextPage,
    fetchNextPage: infiniteScroll.onLoadMore,
    Item: RowItem,
    itemProps: prepareUserRowProps,
    estimateSize: 60,
  })

  return (
    <div
      className={className}
      style={{ height: 'calc(90vh - 280px)' }}
      data-testid="f_list"
    >
      {virtualizedContainer}
    </div>
  )
}

export default VirtualizedScrolledList
